import type { ErrorLike } from '@apollo/client';
import { Flex, Skeleton, SkeletonBox } from '@utilitywarehouse/hearth-react';

import { Basket } from '@components/Basket';
import { CashbackSavingsItem } from '@components/Basket/CashbackSavingsItem';
import { BundleBasketFragment, ProductType } from '@generated/gql/graphql';
import { getMonthlyTotalAfterCashback } from '@utils/price';

import { Item } from './Item';
import { useBasketData } from './useBasketData';
import { useBasketTracking } from './useBasketTracking';

interface DetailsBasketProps {
  basket: BundleBasketFragment;
  cashbackSavingsValue: Nullable<string>;
  error?: ErrorLike;
  isTrackingEnabled?: boolean;
  loading?: boolean;
}

const BasketItemSkeleton = () => (
  <Skeleton loadingTitle="Basket item loading">
    <Flex justifyContent="between">
      <SkeletonBox borderRadius="sm" width="150px" height="22px" />
      <SkeletonBox borderRadius="sm" width="80px" height="22px" />
    </Flex>
  </Skeleton>
);

const DetailsBasket = ({
  loading,
  basket,
  cashbackSavingsValue,
  isTrackingEnabled = false,
}: DetailsBasketProps) => {
  useBasketTracking(isTrackingEnabled);

  const { selectedProducts, addOns, monthlyCost } = useBasketData(
    basket as BundleBasketFragment
  );

  if (loading) {
    return (
      <Basket total="" addOns={[]} loading>
        {new Array<number>(3).fill(0).map((_v, i) => (
          <BasketItemSkeleton key={i} />
        ))}
      </Basket>
    );
  }

  // TODO: Consider some declarative state
  if (!basket) {
    return null;
  }

  const renderAddOns = () => {
    return addOns.map((product) => {
      if (product.type === ProductType.CashbackCard && cashbackSavingsValue) {
        return (
          <>
            <Item key={product.id} product={product} />
            <CashbackSavingsItem cashbackValue={cashbackSavingsValue} />
          </>
        );
      }

      return <Item key={product.id} product={product} />;
    });
  };

  return (
    <Basket
      total={getMonthlyTotalAfterCashback(monthlyCost, cashbackSavingsValue)}
      addOns={renderAddOns()}
    >
      {selectedProducts.map((product) => (
        <Item key={product.id} product={product} />
      ))}
    </Basket>
  );
};

export { DetailsBasket };
